import { BookOpen, Rocket, CreditCard, Send, FileText, LayoutDashboard, ShieldCheck } from "lucide-react";

/**
 * Unified Help Centre content. Shared by the category card grid and the
 * FAQ accordion list so that anchor ids always match.
 *
 * Terminology rules enforced throughout:
 *  - "editorial" never appears (our team is "our PR team")
 *  - "permanent" never appears (use "published and indexed")
 *  - guarantees framed as defined outcomes backed by the full-refund policy
 *  - no em dashes
 */
export const HELP_SECTIONS = [
  {
    id: "pr-basics",
    title: "PR Distribution Basics",
    desc: "What distribution is and how it works",
    icon: BookOpen,
    faqs: [
      {
        q: "What is PR distribution?",
        a: "PR distribution is the process of sending your press release to media outlets, journalists, and news platforms so your announcement reaches its intended audience. Instead of publishing on a single site, distribution puts your story in front of many channels at once, increasing the chance of coverage and pickup. Digital24 offers three distribution channels that work differently: Journalist Outreach, Newswire Distribution, and Guaranteed Placements."
      },
      {
        q: "What is a newswire and how does syndication work?",
        a: "A newswire is a distribution network that syndicates your press release across many outlets simultaneously. When you submit a release, the newswire pushes it to its network of partner sites, which then publish the story on their own pages. This creates broad reach and multiple published links, with Google News indexing included as a standard part of the service."
      },
      {
        q: "What is the difference between earned, syndicated, and guaranteed coverage?",
        a: "Earned coverage (Journalist Outreach) is pitched on merit to journalists who decide whether to write about your story, which is what gives it credibility. Syndicated coverage (Newswire Distribution) is distributed broadly across a network of outlets, providing wide reach and multiple placements. Guaranteed coverage (Guaranteed Placements) is publication on a specific, named site you select, backed by a full-refund policy if it does not go live."
      },
      {
        q: "How do journalists receive and use press releases?",
        a: "Journalists receive press releases through direct pitches, newswire feeds, and media databases. When a story is relevant to their beat, they may write about it, quote from it, or follow up for more information. Coverage is never automatic, which is why earned coverage carries credibility: a journalist chose to cover your story because it was newsworthy."
      },
      {
        q: "What results can I realistically expect from a press release?",
        a: "Results depend on the channel and the strength of your story. Newswire distribution produces broad syndication and multiple published links. Guaranteed placements deliver publication on your chosen sites. Journalist outreach can generate credible earned coverage, but outcomes depend on whether journalists find the story compelling. No channel can promise specific traffic, rankings, or sales, but each delivers a defined, trackable outcome."
      },
      {
        q: "How do I choose between the three channels?",
        a: "If you are unsure which channel fits your goal, the [Find Your Fit guide](/services/find-your-fit) walks you through it based on what you are trying to achieve. It recommends the channel, or combination of channels, built for your objective. You can also explore curated packages that bundle channels for common goals."
      }
    ]
  },
  {
    id: "getting-started",
    title: "Getting Started",
    desc: "The platform, the channels, the basics",
    icon: Rocket,
    faqs: [
      {
        q: "What is Digital24?",
        a: "Digital24 is a press release distribution and digital PR platform. Instead of working through a traditional PR agency on retainer, you buy distribution channels directly: Journalist Outreach, Newswire Distribution, and Guaranteed Placements. You choose what you need, see the price upfront, and track everything from a single dashboard."
      },
      {
        q: "What are the three distribution channels?",
        a: "Journalist Outreach is earned coverage: our team pitches your story to verified journalists by beat and geography. Newswire Distribution is broad syndication across hundreds of outlets, with Google News indexing included as part of the service. Guaranteed Placements publish your article on a specific, named publication that you choose, with a full refund if it does not go live."
      },
      {
        q: "How is Digital24 different from a traditional PR agency?",
        a: "No retainers, no middle layer, no lock-ins. You pay per release and combine channels only when you want to. Pricing is transparent and visible before you commit, and you manage everything through the dashboard rather than over email."
      },
      {
        q: "Do I need an account to browse?",
        a: "No. You can browse the media network, outlet grid, pricing, and packages without signing in. You only need an account when you are ready to place an order or save a draft."
      },
      {
        q: "What is the difference between earned, newswire, and guaranteed coverage?",
        a: "Earned coverage (Journalist Outreach) is pitched on merit to journalists who may or may not pick it up, which is what gives it credibility. Newswire coverage is syndicated broadly across a network of outlets, providing wide distribution and reach. Guaranteed coverage is publication on a specific named site you select, with a refund if it does not appear."
      },
      {
        q: "Which channel should I start with?",
        a: "If you are unsure, use the Find Your Fit tool. It asks what you are trying to achieve and recommends the channel, or combination of channels, built for that goal. You can also explore curated packages that bundle channels for common objectives."
      }
    ]
  },
  {
    id: "orders-pricing",
    title: "Orders & Pricing",
    desc: "Placing orders, currencies, packages",
    icon: CreditCard,
    faqs: [
      {
        q: "How do I place an order?",
        a: "From any page, click Get Started. You will be taken to the new order flow, where you select your channel or package, choose outlets or regions, add your content, and review before payment. You can save a draft at any step and return to it later from your dashboard."
      },
      {
        q: "What currencies do you accept?",
        a: "All orders are billed in US Dollars (USD) via Stripe. We display indicative pricing in GBP and AED for convenience, but the actual charge is processed in USD at the exchange rate applied by your card issuer."
      },
      {
        q: "How is pricing determined?",
        a: "Each channel and outlet is priced individually. Guaranteed Placements are priced per publication, reflecting the outlet's domain authority and tier. Newswire packages are priced per release based on reach and region. Journalist Outreach is priced per campaign. You see the full price before you pay."
      },
      {
        q: "Can I combine channels in a single order?",
        a: "Yes. You can add newswire distribution and guaranteed placements as add-ons to a primary channel, or start from a curated package that bundles them. The order breakdown shows each line item and its price."
      },
      {
        q: "Do you offer ready-made packages?",
        a: "Yes. Our curated packages bundle the channels that work best together for common goals like funding announcements, SEO building, or market entry. Each package has a defined set of inclusions and transparent pricing."
      },
      {
        q: "Is there a minimum order value?",
        a: "No. You can place a single guaranteed placement, a single newswire release, or a full multi-channel campaign. There is no minimum spend."
      },
      {
        q: "Do you offer reseller or wholesale pricing?",
        a: "Yes. We offer wholesale pricing from 20% to 40% off list prices for agencies placing 5 or more orders per month. White-label reporting, multi-client management, and API access are available for qualifying agencies. See the Resellers page or contact us to apply."
      }
    ]
  },
  {
    id: "distribution",
    title: "Distribution & Delivery",
    desc: "Timelines, Google News, tracking",
    icon: Send,
    faqs: [
      {
        q: "How long does distribution take?",
        a: "It depends on the channel. Newswire distribution is typically same-day or within 24 hours of our PR team's review. Guaranteed Placements go live within a few business days, depending on the outlet. Journalist Outreach takes days to weeks, since coverage is pitched on merit. Timelines start from our PR team's review, not from payment."
      },
      {
        q: "Is Google News inclusion guaranteed?",
        a: "Google News indexing is a defined part of our newswire distribution service. Every press release sent through the newswire channel is submitted for Google News indexing at no additional cost. It is built into how the newswire channel works, rather than being a separate promise."
      },
      {
        q: "How do I track my order status?",
        a: "After placing an order, you can track its status in real time through your dashboard. Statuses include Submitted, In Review, Approved, In Progress, Live, and Reported. For multi-channel orders, each channel has its own status so you can see exactly where each part of your campaign stands."
      },
      {
        q: "What does 'live' mean?",
        a: "Live means your article has been published and indexed, and is accessible on the target outlet or outlets. At this stage, the published URL is added to your order and you can view it directly from your dashboard."
      },
      {
        q: "Will I receive a distribution report?",
        a: "Yes. Once distribution is complete, a report is generated showing where your release was published, with live links to each placement. For newswire orders, this includes the full list of outlets that picked up the story."
      },
      {
        q: "Can I choose which outlets my release goes to?",
        a: "For Guaranteed Placements, yes: you select the exact publications and we guarantee placement on each one. For Newswire Distribution, distribution is network-based, meaning your release is syndicated across the wire's full network of outlets rather than to specific named sites."
      },
      {
        q: "What if a guaranteed placement does not go live?",
        a: "If a guaranteed placement does not publish, you receive a full refund for that placement. The guarantee is straightforward: published, or you are refunded in full for that placement."
      }
    ]
  },
  {
    id: "content-submissions",
    title: "Content & Submissions",
    desc: "What to submit, review, variations",
    icon: FileText,
    faqs: [
      {
        q: "What can I submit for distribution?",
        a: "You can submit a press release, a contributor post, or an announcement. Content can be written directly in our rich text editor, or you can upload a document (DOC or PDF) and our team will format it for distribution."
      },
      {
        q: "Is there a review process?",
        a: "Yes. Every submission goes through review by our PR team before distribution. Our team checks for clarity, newsworthiness, formatting, and compliance with outlet guidelines. This ensures your content meets the standards of the publications it will appear on."
      },
      {
        q: "Can my content be rejected?",
        a: "Yes. Content that is promotional rather than newsworthy, contains prohibited claims, or does not meet outlet guidelines may be rejected or returned with requested changes. If changes are requested, you will be notified with specific feedback and can revise and resubmit."
      },
      {
        q: "Can I make changes after submitting?",
        a: "Yes, as long as distribution has not begun. If your order is still in draft, pending payment, or in review, you can edit your content. Once distribution starts, changes are no longer possible because the content has already been sent to outlets."
      },
      {
        q: "What is a content variation?",
        a: "When you run a multi-channel order, each channel may require slightly different content. A variation is a channel-specific version of your press release, anchored to a particular channel or placement. This lets you tailor the angle or length for each audience without creating separate orders."
      },
      {
        q: "Are there restricted topics or industries?",
        a: "We accept press releases from most legitimate industries. We specialise in fintech, crypto, technology, real estate, and luxury, but work across all sectors. Certain niches (such as CBD, gambling, or adult content) are accepted only on specific outlets that permit them. If you are unsure about eligibility, contact us before ordering."
      },
      {
        q: "Can I include images, links, and contact details?",
        a: "Yes. You can attach images with captions, include hyperlinks in the body (dofollow or nofollow depending on the outlet), and add a media contact block. You can choose whether your media contact details are published with the content or shared with journalists only."
      }
    ]
  },
  {
    id: "account-dashboard",
    title: "Account & Dashboard",
    desc: "Sign in, order tracking, messaging",
    icon: LayoutDashboard,
    faqs: [
      {
        q: "How do I create an account?",
        a: "Click Sign In, then Register. Enter your email and create a password, verify with the OTP code sent to your email, and you are in. You can also sign up with Google."
      },
      {
        q: "How do I track my orders?",
        a: "Your dashboard lists every order with its current status. Click any order to see the full breakdown: channels, content, per-channel distribution status, published links, and report files. The dashboard updates in real time as your order moves through the workflow."
      },
      {
        q: "Can I message my PR manager?",
        a: "Yes. Every order has a live messaging thread with your assigned PR manager. You can discuss copy edits, outlet preferences, timelines, and ask questions directly through the dashboard. You will be notified when there is a new message."
      },
      {
        q: "How do I view and download invoices?",
        a: "Go to the Invoices page in your dashboard. Every paid order generates a Stripe invoice that you can view and download as a PDF. The invoice includes the Stripe invoice ID and a direct link to the hosted invoice page."
      },
      {
        q: "How do notifications work?",
        a: "You receive in-app notifications for order status changes, new messages from your PR manager, and important updates. An unread badge appears in the navigation. You can mark notifications as read or access them from the notifications page."
      },
      {
        q: "Can I resume a draft order?",
        a: "Yes. If you started an order but did not complete it, it is saved as a draft in your dashboard. You can resume from exactly where you left off, with your selections and content preserved."
      }
    ]
  },
  {
    id: "policies",
    title: "Policies & Guarantees",
    desc: "Refunds, guarantees, resellers",
    icon: ShieldCheck,
    faqs: [
      {
        q: "What is your refund policy?",
        a: "We offer a full refund if your release has not yet entered the distribution phase. Once distribution begins, refunds are not available. However, guaranteed placements are covered by our published-or-refunded policy: if a placement does not go live, you receive a full refund for that placement. See our Refund Policy for full details."
      },
      {
        q: "Is guaranteed placement really guaranteed?",
        a: "Yes. When you purchase a guaranteed placement, the defined outcome is publication on the named outlet. If it is not published, you receive a full refund for that placement. There are no partial credits or substitution offers: it is published, or you receive a full refund."
      },
      {
        q: "What types of links do placements include?",
        a: "Guaranteed placements specify whether the backlink is dofollow or nofollow, and this is shown on the outlet before you order. Most placements include a dofollow backlink, which is valuable for SEO. The link type is part of the publication's listing and cannot be changed after ordering."
      },
      {
        q: "Do you support embargoes?",
        a: "Yes. If your announcement is under embargo, let your PR manager know through the order messaging thread and specify the embargo date and time. We will coordinate distribution so your release goes live at the correct moment."
      },
      {
        q: "What happens if I request changes to my order?",
        a: "If our PR team requests changes to your content, your order enters a changes-requested state. You will be notified with specific feedback, and distribution is paused until you revise and resubmit. Once cleared, the order resumes its normal workflow."
      },
      {
        q: "Where can I find our full legal policies?",
        a: "Our Terms & Conditions, Privacy Policy, and Refund Policy are linked in the footer of every page. These cover the complete legal framework for using the platform and placing orders."
      }
    ]
  }
];